<template>
  <article class="kpi-card" :class="{ 'kpi-card--with-breakdown': hasBreakdown }">
    <div class="kpi-icon" :style="iconStyle">
      <i class="fa" :class="icon" aria-hidden="true"></i>
    </div>

    <div class="kpi-copy">
      <p>{{ title }}</p>
      <div class="kpi-values">
        <h3
          v-if="normalizedValueSegments.length === 0"
          :style="valueStyle"
          :class="{ 'kpi-value-truncated': truncateValue }"
          :title="truncateValue ? value : ''"
        >
          <router-link
            v-if="hasValueRoute"
            :to="valueRoute"
            class="kpi-value-link"
            :class="[valueLinkClass, { 'kpi-value-link-truncated': truncateValue }]"
            :title="truncateValue ? value : ''"
          >
            {{ value }}
          </router-link>
          <span
            v-else
            class="kpi-value-text"
            :class="{ 'kpi-value-text-truncated': truncateValue }"
            :title="truncateValue ? value : ''"
          >
            {{ value }}
          </span>
        </h3>

        <div
          v-else
          class="kpi-split-values"
          :class="{
            'kpi-split-values--single': normalizedValueSegments.length <= 1,
            'kpi-split-values--multi': normalizedValueSegments.length > 1,
          }"
          :style="valueStyle"
        >
          <span
            v-for="(segment, index) in normalizedValueSegments"
            :key="`${title}-segment-${index}`"
            :title="segment"
          >
            {{ segment }}
          </span>
        </div>

        <small
          v-if="normalizedValueMetaItems.length > 0 && normalizedValueMeta"
          class="kpi-value-meta"
        >
          {{ normalizedValueMeta }}
        </small>

        <div v-if="normalizedValueMetaItems.length > 0" class="kpi-value-meta-grid">
          <div
            v-for="(item, index) in normalizedValueMetaItems"
            :key="`meta-item-${title}-${index}`"
            class="kpi-value-meta-row"
            :class="`kpi-value-meta-row--${item.tone || 'base'}`"
          >
            <small class="kpi-value-meta-label">{{ item.label }}</small>
            <strong class="kpi-value-meta-value">{{ item.value }}</strong>
          </div>
        </div>

        <small v-else-if="normalizedValueMetaLines.length === 0 && normalizedValueMeta" class="kpi-value-meta">{{ normalizedValueMeta }}</small>
        <div v-else-if="normalizedValueMetaLines.length > 0" class="kpi-value-meta-lines">
          <small
            v-for="(line, index) in normalizedValueMetaLines"
            :key="`meta-${title}-${index}`"
            class="kpi-value-meta"
            :class="`kpi-value-meta--${line.tone || 'base'}`"
          >
            {{ line.text }}
          </small>
        </div>

        <ul v-if="normalizedExtraValues.length > 0" class="kpi-extra-values">
          <li v-for="(line, index) in normalizedExtraValues" :key="`extra-${title}-${index}`">
            {{ line }}
          </li>
        </ul>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'ExpenseReceiptsKpiCard',
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    valueRoute: {
      type: [Object, String],
      default: null,
    },
    valueLinkClass: {
      type: String,
      default: '',
    },
    valueScale: {
      type: Number,
      default: 1,
    },
    truncateValue: {
      type: Boolean,
      default: false,
    },
    valueMeta: {
      type: String,
      default: '',
    },
    valueMetaLines: {
      type: Array,
      default: () => [],
    },
    valueMetaItems: {
      type: Array,
      default: () => [],
    },
    extraValues: {
      type: Array,
      default: () => [],
    },
    valueSegments: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: String,
      default: 'fa-circle',
    },
    iconBg: {
      type: String,
      default: '#e8f3ff',
    },
    iconColor: {
      type: String,
      default: '#0070cd',
    },
  },
  computed: {
    iconStyle() {
      return {
        background: this.iconBg,
        color: this.iconColor,
      };
    },
    hasValueRoute() {
      if (typeof this.valueRoute === 'string') {
        return String(this.valueRoute || '').trim() !== '';
      }

      return !!this.valueRoute && typeof this.valueRoute === 'object';
    },
    valueStyle() {
      const safeScale = Number(this.valueScale);
      const scale = Number.isFinite(safeScale) && safeScale > 0 ? safeScale : 1;

      return {
        fontSize: `${24 * scale}px`,
      };
    },
    normalizedExtraValues() {
      return (Array.isArray(this.extraValues) ? this.extraValues : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    },
    normalizedValueMeta() {
      return String(this.valueMeta || '').trim();
    },
    normalizedValueMetaLines() {
      return (Array.isArray(this.valueMetaLines) ? this.valueMetaLines : [])
        .map((item) => {
          if (item && typeof item === 'object') {
            const text = String(item.text || item.value || '').trim();
            const tone = String(item.tone || '').trim().toLowerCase();
            if (text === '') {
              return null;
            }

            return {
              text,
              tone: ['warning', 'muted'].includes(tone) ? tone : 'base',
            };
          }

          const text = String(item || '').trim();
          if (text === '') {
            return null;
          }

          return {
            text,
            tone: 'base',
          };
        })
        .filter(Boolean);
    },
    normalizedValueMetaItems() {
      return (Array.isArray(this.valueMetaItems) ? this.valueMetaItems : [])
        .map((item) => {
          if (!item || typeof item !== 'object') {
            return null;
          }

          const label = String(item.label || '').trim();
          const value = String(item.value || '').trim();
          const tone = String(item.tone || '').trim().toLowerCase();
          if (label === '' || value === '') {
            return null;
          }

          return {
            label,
            value,
            tone: tone === 'plus' || tone === 'minus' ? tone : 'base',
          };
        })
        .filter(Boolean);
    },
    normalizedValueSegments() {
      return (Array.isArray(this.valueSegments) ? this.valueSegments : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    },
    hasBreakdown() {
      return this.normalizedValueMetaItems.length > 0 || this.normalizedValueMetaLines.length >= 3;
    },
  },
};
</script>

<style scoped>
.kpi-card {
  background: #ffffff;
  border: 1px solid rgba(153, 152, 153, 0.2);
  border-radius: 9px;
  box-shadow: 0 3px 12px rgba(0, 98, 163, 0.08);
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 7.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.kpi-copy p {
  margin: 0;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
  color: #999899;
  letter-spacing: 0.02em;
}

.kpi-copy {
  flex: 1;
  min-width: 0;
}

.kpi-values {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  min-width: 0;
}

.kpi-card--with-breakdown {
  align-items: flex-start;
}

.kpi-card--with-breakdown .kpi-values {
  gap: 3px;
}

.kpi-card--with-breakdown .kpi-values h3 {
  margin-top: 1px;
  line-height: 1.15;
}

.kpi-card--with-breakdown .kpi-value-meta-lines {
  margin-top: 4px;
  gap: 2px;
}

.kpi-card--with-breakdown .kpi-value-meta-grid {
  margin-top: 7px;
  gap: 3px;
}

.kpi-card--with-breakdown .kpi-value-meta {
  font-size: 10px;
  line-height: 1.15;
}

.kpi-card--with-breakdown .kpi-value-meta-label,
.kpi-card--with-breakdown .kpi-value-meta-value {
  font-size: 11px;
  line-height: 1.15;
}

.kpi-values h3 {
  margin: 2px 0 0;
  font-weight: 700;
  color: #494949;
  line-height: 1.2;
  width: 100%;
  min-width: 0;
}

.kpi-value-link {
  color: inherit;
  text-decoration: underline;
}

.kpi-value-text {
  color: inherit;
}

.kpi-value-link:hover {
  color: inherit;
}

.kpi-value-link-supplier {
  color: #0070cd;
  text-decoration: none;
}

.kpi-value-link-supplier:hover {
  color: #0070cd;
  text-decoration: none;
}

.kpi-value-truncated {
  overflow: hidden;
}

.kpi-value-link-truncated,
.kpi-value-text-truncated {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.kpi-value-meta {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  text-transform: none;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.2;
}

.kpi-value-meta--warning {
  color: #b45309;
  font-weight: 600;
}

.kpi-value-meta--muted {
  color: #6b7280;
}

.kpi-value-meta-lines {
  display: grid;
  gap: 1px;
  margin-top: 2px;
}

.kpi-value-meta-grid {
  display: grid;
  gap: 1px;
  margin-top: 2px;
  width: 100%;
}

.kpi-value-meta-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.kpi-value-meta-label {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.kpi-value-meta-value {
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  text-align: right;
  white-space: nowrap;
}

.kpi-value-meta-row--plus .kpi-value-meta-label {
  color: #2563eb;
}

.kpi-value-meta-row--minus .kpi-value-meta-label {
  color: #9a3412;
}

.kpi-split-values {
  margin: 2px 0 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  font-weight: 700;
  color: #494949;
  line-height: 1.2;
}

.kpi-split-values--single {
  gap: 0;
}

.kpi-split-values--multi {
  font-size: 14px !important;
  font-weight: 600;
}

.kpi-split-values > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-extra-values {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 2px;
  color: #494949;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
</style>
